import type { GifResult, GifMode } from '@/components/gif-picker/types';

const BASE_URL = 'https://api.klipy.com/api/v1';

function apiKey(): string {
  return import.meta.env.VITE_KLIPY_API_KEY ?? '';
}

export interface GifPage {
  results: GifResult[];
  /**
   * Klipy doesn't return a total count — we synthesise one so existing
   * pagination logic (hasMore = results.length < totalCount) still works.
   * Sentinel: if `has_next` is true we add 1 beyond the current window,
   * otherwise totalCount equals the number of items seen so far.
   */
  totalCount: number;
}

interface KlipySize {
  url: string;
  width: number;
  height: number;
}

interface KlipyFile {
  hd: { gif: KlipySize; webp: KlipySize; jpg?: KlipySize; png?: KlipySize };
  md: { gif: KlipySize; webp: KlipySize; jpg?: KlipySize; png?: KlipySize };
  sm: { gif: KlipySize; webp: KlipySize; jpg?: KlipySize; png?: KlipySize };
  xs: { gif: KlipySize; webp: KlipySize; jpg?: KlipySize; png?: KlipySize };
}

interface KlipyItem {
  id: number | string;
  title: string;
  file: KlipyFile;
}

interface KlipyResponse {
  result: boolean;
  data: {
    data: KlipyItem[];
    current_page: number;
    per_page: number;
    has_next: boolean;
  };
}

function mapItem(item: KlipyItem, mode: GifMode): GifResult {
  const md = item.file.md;
  const sm = item.file.sm;
  const hd = item.file.hd;

  // Stickers: use transparent PNG still (hd) and animated WebP for placement
  // GIFs: use small JPG still, medium animated GIF for placement
  const isSticker = mode === 'stickers';
  const stillUrl = isSticker
    ? (hd.png?.url ?? hd.gif.url)
    : (sm.jpg?.url ?? sm.gif.url);
  const webpUrl = md.webp.url;

  return {
    id: String(item.id),
    title: item.title,
    url: md.gif.url,
    stillUrl,
    previewUrl: sm.gif.url,
    webpUrl,
    width: md.gif.width,
    height: md.gif.height,
  };
}

export async function fetchGifs(
  mode: GifMode,
  query: string,
  limit = 20,
  offset = 0,
): Promise<GifPage> {
  const key = apiKey();
  const segment = mode === 'gifs' ? 'gifs' : 'stickers';
  const action = query ? 'search' : 'trending';
  const endpoint = `${BASE_URL}/${key}/${segment}/${action}`;

  // Klipy uses 1-indexed pages, not offsets
  const page = Math.floor(offset / limit) + 1;

  const params = new URLSearchParams({
    per_page: String(limit),
    page: String(page),
  });
  if (query) params.set('q', query);

  const res = await fetch(`${endpoint}?${params}`);
  if (!res.ok) throw new Error(`Klipy API error: ${res.status}`);

  const json = await res.json() as KlipyResponse;
  const { data: items, has_next } = json.data;

  const results = items.map(item => mapItem(item, mode));

  // Synthesise a totalCount that makes GifPicker's hasMore work correctly
  const seen = offset + results.length;
  const totalCount = has_next ? seen + 1 : seen;

  return { results, totalCount };
}
